import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';

export default function AboutPage() {
  const currentDate = new Date().toISOString();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'About Us | Hoosier Life Insurance | Indianapolis, Indiana';
    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute('content', content);
    };
    setMeta('description', 'Meet the licensed life insurance specialists at Hoosier Life Insurance. Indiana-licensed team, NAIFA members, 20+ A-rated carriers, serving all of Indiana since 2018.');
    setMeta('last-modified', new Date().toISOString());

    const canonical = document.querySelector('link[rel="canonical"]');
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://hoosierlifeinsurance.com';
    if (canonical) {
      canonical.setAttribute('href', `${siteUrl}/about`);
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = `${siteUrl}/about`;
      document.head.appendChild(link);
    }

    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.id = 'about-page-schema';
    schemaScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/about#webpage`,
          url: `${siteUrl}/about`,
          name: 'About Hoosier Life Insurance | Licensed Indiana Life Insurance Team',
          description: 'Meet the licensed life insurance specialists at Hoosier Life Insurance. Indiana-licensed, NAIFA members, independent — 20+ A-rated carriers. Serving all of Indiana since 2018.',
          inLanguage: 'en-US',
          datePublished: '2018-01-01T00:00:00+00:00',
          dateModified: currentDate,
          isPartOf: { '@id': `${siteUrl}/#website` },
          about: { '@id': `${siteUrl}/#organization` },
          breadcrumb: { '@id': `${siteUrl}/about#breadcrumb` },
          speakable: {
            '@type': 'SpeakableSpecification',
            cssSelector: ['h1', '#about-team-heading', '#about-credentials-heading']
          }
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${siteUrl}/about#breadcrumb`,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
            { '@type': 'ListItem', position: 2, name: 'About Us', item: `${siteUrl}/about` }
          ]
        },
        {
          '@type': 'InsuranceAgency',
          '@id': `${siteUrl}/#organization`,
          name: 'Hoosier Life Insurance',
          url: siteUrl,
          description: 'Independent life insurance agency serving all of Indiana by phone and virtual consultation. Team of licensed specialists offering term life, whole life, final expense, IUL, annuities, and mortgage protection.',
          foundingDate: '2018',
          slogan: 'Indiana\'s Independent Life Insurance Team — Phone & Virtual Consultations Statewide',
          areaServed: { '@type': 'State', name: 'Indiana' },
          serviceType: 'Life Insurance — Phone & Virtual Consultations Statewide',
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: '4.9',
            reviewCount: '127',
            bestRating: '5',
            worstRating: '1'
          },
          hasCredential: [
            {
              '@type': 'EducationalOccupationalCredential',
              credentialCategory: 'Indiana Life Insurance License',
              recognizedBy: {
                '@type': 'Organization',
                name: 'Indiana Department of Insurance',
                url: 'https://www.in.gov/idoi/'
              },
              validIn: { '@type': 'State', name: 'Indiana' }
            },
            {
              '@type': 'EducationalOccupationalCredential',
              credentialCategory: 'Indiana Health Insurance License',
              recognizedBy: {
                '@type': 'Organization',
                name: 'Indiana Department of Insurance',
                url: 'https://www.in.gov/idoi/'
              },
              validIn: { '@type': 'State', name: 'Indiana' }
            }
          ],
          memberOf: {
            '@type': 'Organization',
            name: 'National Association of Insurance and Financial Advisors (NAIFA)',
            url: 'https://www.naifa.org'
          },
          knowsAbout: [
            'Term Life Insurance Indiana',
            'Whole Life Insurance Indiana',
            'Final Expense Insurance Indiana',
            'Indexed Universal Life Insurance Indiana',
            'Annuities Indiana',
            'Mortgage Protection Insurance Indiana',
            'Life Insurance for Seniors Indiana',
            'No Medical Exam Life Insurance Indiana',
            'Life Insurance for Diabetics Indiana',
            'Business Key Person Insurance Indianapolis',
            'Burial Insurance Indiana',
            'Life Insurance for Self-Employed Indiana'
          ],
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Indiana Life Insurance Products',
            itemListElement: [
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Term Life Insurance', url: `${siteUrl}/term-life` } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Whole Life Insurance', url: `${siteUrl}/whole-life` } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Final Expense Insurance', url: `${siteUrl}/final-expense` } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Indexed Universal Life Insurance', url: `${siteUrl}/indexed-universal-life` } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Annuities', url: `${siteUrl}/annuities` } }
            ]
          },
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'sales',
            telephone: '+1-317-648-2983',
            email: 'info@hoosierlifeinsurance.com',
            availableLanguage: 'English',
            hoursAvailable: {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '09:00',
              closes: '17:00'
            },
            contactOption: 'TollFree',
            areaServed: { '@type': 'State', name: 'Indiana' }
          }
        },
        {
          '@type': 'Person',
          '@id': `${siteUrl}/#specialist-lead`,
          name: 'Licensed Life Insurance Specialist',
          jobTitle: 'Senior Life Insurance Specialist',
          worksFor: { '@id': `${siteUrl}/#organization` },
          description: 'Licensed Indiana life insurance specialist with 12+ years of experience in term life, whole life, final expense, IUL, and annuities. Independent — works with 20+ A-rated carriers.',
          hasCredential: [
            {
              '@type': 'EducationalOccupationalCredential',
              credentialCategory: 'Indiana Life Insurance License',
              recognizedBy: {
                '@type': 'Organization',
                name: 'Indiana Department of Insurance',
                url: 'https://www.in.gov/idoi/'
              },
              validIn: { '@type': 'State', name: 'Indiana' }
            },
            {
              '@type': 'EducationalOccupationalCredential',
              credentialCategory: 'NAIFA Member',
              recognizedBy: {
                '@type': 'Organization',
                name: 'National Association of Insurance and Financial Advisors'
              }
            }
          ],
          knowsAbout: [
            'Term Life Insurance',
            'Whole Life Insurance',
            'Final Expense Insurance',
            'Indexed Universal Life Insurance',
            'Annuities',
            'Mortgage Protection Insurance',
            'Life Insurance Underwriting',
            'Estate Planning with Life Insurance',
            'Business Succession Planning'
          ],
          alumniOf: {
            '@type': 'Organization',
            name: 'National Association of Insurance and Financial Advisors (NAIFA)'
          }
        },
        {
          '@type': 'Person',
          '@id': `${siteUrl}/#specialist-2`,
          name: 'Licensed Life Insurance Specialist',
          jobTitle: 'Life Insurance Specialist — Senior & Final Expense',
          worksFor: { '@id': `${siteUrl}/#organization` },
          description: 'Indiana-licensed specialist in final expense insurance, burial insurance, and senior life insurance. Helps Indiana families ages 50-85 find affordable coverage with no medical exam.',
          hasCredential: [
            {
              '@type': 'EducationalOccupationalCredential',
              credentialCategory: 'Indiana Life Insurance License',
              recognizedBy: {
                '@type': 'Organization',
                name: 'Indiana Department of Insurance',
                url: 'https://www.in.gov/idoi/'
              },
              validIn: { '@type': 'State', name: 'Indiana' }
            }
          ],
          knowsAbout: [
            'Final Expense Insurance',
            'Burial Insurance',
            'Guaranteed Issue Life Insurance',
            'Senior Life Insurance Indiana',
            'No Medical Exam Life Insurance'
          ]
        },
        {
          '@type': 'Person',
          '@id': `${siteUrl}/#specialist-3`,
          name: 'Licensed Life Insurance Specialist',
          jobTitle: 'Life Insurance Specialist — IUL & Annuities',
          worksFor: { '@id': `${siteUrl}/#organization` },
          description: 'Indiana-licensed specialist in Indexed Universal Life insurance and fixed annuities. Helps Indiana families build tax-deferred wealth with market-linked growth and principal protection.',
          hasCredential: [
            {
              '@type': 'EducationalOccupationalCredential',
              credentialCategory: 'Indiana Life Insurance License',
              recognizedBy: {
                '@type': 'Organization',
                name: 'Indiana Department of Insurance',
                url: 'https://www.in.gov/idoi/'
              },
              validIn: { '@type': 'State', name: 'Indiana' }
            }
          ],
          knowsAbout: [
            'Indexed Universal Life Insurance',
            'Fixed Annuities',
            'Indexed Annuities',
            'Retirement Income Planning',
            'Tax-Deferred Wealth Accumulation',
            'Business Key Person Insurance'
          ]
        }
      ]
    });
    document.head.appendChild(schemaScript);

    return () => {
      const existingSchema = document.getElementById('about-page-schema');
      if (existingSchema) {
        existingSchema.remove();
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="bg-[#1A3A52] border-b border-[#1A3A52]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <ol className="flex items-center space-x-2 text-sm">
            <li>
              <a href="/" className="text-white/70 hover:text-[#D97642] transition-colors">
                Home
              </a>
            </li>
            <li className="text-white/40">/</li>
            <li className="text-[#D97642] font-medium">About Us</li>
          </ol>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] text-white py-20 lg:py-28">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Protecting Indiana Families Since 2018
            </h1>
            <p className="text-xl text-gray-200 mb-8 leading-relaxed">
              We're an independent life insurance agency dedicated to helping Hoosier families secure their financial future. Our mission is simple: provide honest guidance, personalized solutions, and unwavering support when you need it most.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/book-appointment"
                className="inline-flex items-center px-8 py-4 bg-[#D97642] text-white font-semibold rounded-lg hover:bg-[#C26638] transition-colors whitespace-nowrap"
                aria-label="Schedule a consultation with Hoosier Life Insurance"
              >
                Schedule a Consultation
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center px-8 py-4 bg-white/15 text-white font-semibold rounded-lg hover:bg-white/25 transition-colors border border-white/30 whitespace-nowrap"
                aria-label="Contact Hoosier Life Insurance"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold text-[#1A3A52] mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  We started Hoosier Life Insurance in 2018 because we were tired of seeing Indiana families get confused, pressured, and sold the wrong policy. Life insurance should be simple, honest, and affordable — and we made it our mission to make that happen.
                </p>
                <p>
                  Here's the big thing: we're <strong>independent</strong>. That means we don't work for any insurance company. We work for <strong>you</strong>. We shop 20+ A-rated carriers — Mutual of Omaha, Prudential, Transamerica, Nationwide, and more — and find you the best rate. Not the best commission for us. The best deal for your family.
                </p>
                <p>
                  Today we protect 500+ families across Indiana — from young couples buying their first home in Fishers to retirees in Carmel planning their estate. Every single client gets the same thing: straight answers, no pressure, and a policy that actually makes sense for their life.
                </p>
              </div>
            </div>
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/a3d3a59c56e2c2c187dc151bae3161da.jpg"
                alt="Benjamin Wright, licensed life insurance agent in Indianapolis Indiana, meeting with a local family to review their coverage options"
                title="Hoosier Life Insurance — Trusted Indiana Life Insurance Agent Meeting with Local Family"
                className="w-full h-full object-cover rounded-lg shadow-xl"
                loading="lazy"
                decoding="async"
                width={600}
                height={700}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#1A3A52] mb-12 text-center">
            Our Journey
          </h2>
          <div className="space-y-8">
            {/* 2018 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-24 text-right">
                <span className="text-2xl font-bold text-[#D97642]">2018</span>
              </div>
              <div className="flex-shrink-0 w-1 bg-[#D97642] rounded-full"></div>
              <div className="flex-1 pb-8">
                <h3 className="text-xl font-bold text-[#1A3A52] mb-2">Founded</h3>
                <p className="text-gray-700">
                  Hoosier Life Insurance opens its doors with a mission to serve Indiana families with honest, independent life insurance guidance.
                </p>
              </div>
            </div>

            {/* 2019 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-24 text-right">
                <span className="text-2xl font-bold text-[#D97642]">2019</span>
              </div>
              <div className="flex-shrink-0 w-1 bg-[#D97642] rounded-full"></div>
              <div className="flex-1 pb-8">
                <h3 className="text-xl font-bold text-[#1A3A52] mb-2">Expanded Carrier Network</h3>
                <p className="text-gray-700">
                  Partnered with 6 top-rated insurance carriers to offer clients more choice and competitive pricing across term life, whole life, and final expense policies.
                </p>
              </div>
            </div>

            {/* 2020 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-24 text-right">
                <span className="text-2xl font-bold text-[#D97642]">2020</span>
              </div>
              <div className="flex-shrink-0 w-1 bg-[#D97642] rounded-full"></div>
              <div className="flex-1 pb-8">
                <h3 className="text-xl font-bold text-[#1A3A52] mb-2">100+ Families Protected</h3>
                <p className="text-gray-700">
                  Reached a major milestone by serving over 100 Indiana families, proving that independent, client-first service resonates with Hoosiers.
                </p>
              </div>
            </div>

            {/* 2021 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-24 text-right">
                <span className="text-2xl font-bold text-[#D97642]">2021</span>
              </div>
              <div className="flex-shrink-0 w-1 bg-[#D97642] rounded-full"></div>
              <div className="flex-1 pb-8">
                <h3 className="text-xl font-bold text-[#1A3A52] mb-2">NAIFA Membership</h3>
                <p className="text-gray-700">
                  Joined the National Association of Insurance and Financial Advisors (NAIFA) to stay at the forefront of industry best practices and ethical standards.
                </p>
              </div>
            </div>

            {/* 2023 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-24 text-right">
                <span className="text-2xl font-bold text-[#D97642]">2023</span>
              </div>
              <div className="flex-shrink-0 w-1 bg-[#D97642] rounded-full"></div>
              <div className="flex-1 pb-8">
                <h3 className="text-xl font-bold text-[#1A3A52] mb-2">Statewide Service</h3>
                <p className="text-gray-700">
                  Expanded to serve 24+ Indiana communities, from major cities to rural towns, bringing personalized life insurance solutions to every corner of the state.
                </p>
              </div>
            </div>

            {/* 2025 */}
            <div className="flex gap-6">
              <div className="flex-shrink-0 w-24 text-right">
                <span className="text-2xl font-bold text-[#D97642]">2025</span>
              </div>
              <div className="flex-shrink-0 w-1 bg-[#D97642] rounded-full"></div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-[#1A3A52] mb-2">500+ Families &amp; Growing</h3>
                <p className="text-gray-700">
                  Proudly protecting over 500 Indiana families with a 5.0-star average rating. Our commitment to education, transparency, and long-term relationships continues to drive our growth.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet Your Agent */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="about-team-heading" className="text-3xl lg:text-4xl font-bold text-[#1A3A52] mb-12 text-center">
            Meet Your Agent
          </h2>
          <div className="max-w-4xl mx-auto bg-gray-50 rounded-xl p-8 lg:p-12 shadow-lg">
            <div className="grid lg:grid-cols-3 gap-8 items-start">
              <div className="lg:col-span-1">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20male%20insurance%20agent%20in%20business%20attire%20smiling%20confident%20trustworthy%20Indiana%20licensed%20professional%20headshot%20portrait%20studio%20lighting&width=400&height=500&seq=agent-photo&orientation=portrait"
                  alt="Benjamin Wright, Licensed Insurance Agent"
                  title="Benjamin Wright — Licensed Life Insurance Agent at Hoosier Life Insurance, Indianapolis, Indiana"
                  className="w-full h-auto object-cover rounded-lg shadow-md"
                  loading="lazy"
                  decoding="async"
                  width={400}
                  height={500}
                />
              </div>
              <div className="lg:col-span-2">
                <h3 className="text-2xl font-bold text-[#1A3A52] mb-2">
                  Benjamin Wright
                </h3>
                <p className="text-[#D97642] font-semibold mb-4">
                  Independent Insurance Agent | Indiana Licensed
                </p>
                <div className="space-y-4 text-gray-700 leading-relaxed mb-6">
                  <p>
                    With 12 years of experience in the life insurance industry, our licensed agents are dedicated to helping Indiana families find the right coverage without the pressure or confusion.
                  </p>
                  <p>
                    We believe in education first. Every consultation starts with understanding your goals, your family's needs, and your budget. Then we compare policies from multiple carriers to find the best fit — not the highest commission.
                  </p>
                  <p>
                    Our agents hold active Indiana life insurance licenses and maintain memberships with NAIFA (National Association of Insurance and Financial Advisors) to stay current on industry regulations, product innovations, and ethical standards.
                  </p>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <p><strong>Credentials:</strong> Indiana Life Insurance License</p>
                  <p><strong>Memberships:</strong> NAIFA (National Association of Insurance and Financial Advisors)</p>
                  <p><strong>Specialties:</strong> Term Life, Whole Life, Final Expense, Annuities</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#1A3A52] mb-12 text-center">
            What We Stand For
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Local */}
            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#D97642]/10 rounded-full flex items-center justify-center mb-6">
                <i className="ri-map-pin-line text-3xl text-[#D97642]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1A3A52] mb-4">Local &amp; Accessible</h3>
              <p className="text-gray-700 leading-relaxed">
                We're your neighbors. We live in Indiana, serve Indiana families, and understand the unique needs of Hoosier communities. You'll always work with a local agent who knows your area.
              </p>
            </div>

            {/* Independent */}
            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#D97642]/10 rounded-full flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-[#D97642]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1A3A52] mb-4">Independent &amp; Unbiased</h3>
              <p className="text-gray-700 leading-relaxed">
                We're not tied to any single insurance company. We compare policies from 6+ top-rated carriers to find the best coverage and price for your situation — not the highest commission for us.
              </p>
            </div>

            {/* No Pressure */}
            <div className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-[#D97642]/10 rounded-full flex items-center justify-center mb-6">
                <i className="ri-hand-heart-line text-3xl text-[#D97642]"></i>
              </div>
              <h3 className="text-xl font-bold text-[#1A3A52] mb-4">No-Pressure Guidance</h3>
              <p className="text-gray-700 leading-relaxed">
                We believe in education, not sales tactics. Take your time, ask questions, and make an informed decision. We're here to guide you, not pressure you into a policy you don't need.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Signals */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 id="about-credentials-heading" className="text-3xl lg:text-4xl font-bold text-[#1A3A52] mb-12 text-center">
            Trusted by Indiana Families
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* 500+ Families */}
            <div className="text-center">
              <div className="text-5xl font-bold text-[#D97642] mb-2">500+</div>
              <p className="text-gray-700 font-medium">Families Protected</p>
            </div>

            {/* Years of Experience */}
            <div className="text-center">
              <div className="text-5xl font-bold text-[#D97642] mb-2">12</div>
              <p className="text-gray-700 font-medium">Years of Experience</p>
            </div>

            {/* 20+ Carriers */}
            <div className="text-center">
              <div className="text-5xl font-bold text-[#D97642] mb-2">20+</div>
              <p className="text-gray-700 font-medium">A-Rated Carriers</p>
            </div>

            {/* 5.0 Rating */}
            <div className="text-center">
              <div className="text-5xl font-bold text-[#D97642] mb-2">5.0★</div>
              <p className="text-gray-700 font-medium">Average Rating</p>
            </div>
          </div>

          {/* Carrier Logos */}
          <div className="mt-16">
            <p className="text-center text-gray-600 font-medium mb-8">
              We partner with 20+ top-rated insurance carriers:
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
              <div className="text-2xl font-bold text-gray-700">Mutual of Omaha</div>
              <div className="text-2xl font-bold text-gray-700">Transamerica</div>
              <div className="text-2xl font-bold text-gray-700">Foresters</div>
              <div className="text-2xl font-bold text-gray-700">AIG</div>
              <div className="text-2xl font-bold text-gray-700">Prudential</div>
              <div className="text-2xl font-bold text-gray-700">Lincoln Financial</div>
            </div>
          </div>

          {/* NAIFA Badge */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-gray-50 px-6 py-4 rounded-lg">
              <i className="ri-award-line text-3xl text-[#D97642]"></i>
              <div className="text-left">
                <p className="font-bold text-[#1A3A52]">NAIFA Member</p>
                <p className="text-sm text-gray-600">National Association of Insurance and Financial Advisors</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            Ready to Protect Your Family?
          </h2>
          <p className="text-xl text-white/80 mb-8 leading-relaxed">
            Schedule a free consultation with a licensed Indiana agent. No pressure, no obligations — just honest guidance and personalized solutions.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/book-appointment"
              className="inline-flex items-center px-8 py-4 bg-[#D97642] text-white font-semibold rounded-lg hover:bg-[#C26638] transition-colors whitespace-nowrap"
            >
              <i className="ri-calendar-line text-xl mr-2"></i>
              Book a Free Consultation
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-white/15 text-white font-semibold rounded-lg hover:bg-white/25 transition-colors border border-white/30 whitespace-nowrap"
            >
              <i className="ri-phone-line text-xl mr-2"></i>
              Call Us Today
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}